import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { imageUrl } from 'js/utils/image_url';
import { getCitingSources, CitingSource } from 'js/profiles/api/mentions_api';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-12, 12px);

  padding: var(--spacing-xs-16, 16px);
  border: 1px solid var(--stroke-dark, #141413);
  border-radius: var(--border-radius-radius-lg, 16px);
  background: var(--background-beige-med, #f2f0ec);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4, 4px);
  word-break: break-word;
`;

// `bold` alone renders at regular weight sitewide: .ds2-5-body-md-bold's weight token is
// undefined and has no fallback. Drop this override when AIC-228 fixes the token.
const Title = styled(Body).attrs({ as: 'span', size: 'md', bold: true })`
  font-weight: var(--type-body-md-bold-font-weight, 600);
`;

const Divider = styled.div`
  height: 1px;
  align-self: stretch;
  background: var(--background-beige-dark, #e2e0d9);
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;
`;

// Stock portraits, not the real citers — never source these from the payload.
// No leading `/images/`: that makes `imageUrl` pass the path through and skip the CDN.
const PORTRAITS = [
  'citing_sources/person-1.jpg',
  'citing_sources/person-2.jpg',
  'citing_sources/person-3.jpg',
];

const AvatarSlot = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  width: 40px;
  height: 40px;
  border-radius: 100px;
  background: var(--background-beige-dark, #e2e0d9);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Descriptor = styled.div`
  flex: 1 0 0;
  min-width: 0;
  word-break: break-word;
  color: var(--text-primary, #141413);
`;

const Secondary = styled.span`
  color: var(--text-secondary, #535366);
`;

// The `citations-` prefix gates CheckoutController#citations_checkout_entry? — do not rename.
const CTA_TRIGGER = 'citations-promo-profile-rail';

// `top_ids`, not `top_mention_ids` — the latter is the legacy /mentions param, ignored here.
// `_url`, not `_path`: /citations is declared inside `subdomain :www`, but this card renders on
// the profile page, which only routes under ProfileConstraint — i.e. always on a non-www
// subdomain (`independent.` when the owner has no affiliation). A host-relative /citations would
// 404 there. Same reason the navbar and the sibling home card both use citations_url.
const citationHref = (citationId: number) =>
  routes.citations_url({ top_ids: citationId, trigger: CTA_TRIGGER });

export const CitingSourcesCard: React.FC<{ userId: number | string }> = ({
  userId,
}) => {
  const [rows, setRows] = useState<CitingSource[] | null>(null);

  // One fetch per userId — no polling, no retry. Null until resolved, and empty renders nothing.
  useEffect(() => {
    let cancelled = false;
    getCitingSources(String(userId))
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (rows == null || rows.length === 0) return null;

  return (
    <ImpressionWatcher
      source="profile.citing_sources_card"
      extra={{ rows: rows.length }}
    >
      <Card>
        <Header>
          <TitleRow>
            <Title>Who is citing your work</Title>
            <Body size="xs" as="span" aria-hidden="true">
              |
            </Body>
            <Body size="xs" as="span">
              <Secondary>Private to you</Secondary>
            </Body>
          </TitleRow>
          <Divider />
        </Header>
        <Rows>
          {rows.map((row, index) => {
            const portrait = PORTRAITS[index % PORTRAITS.length];

            return (
              <Row key={row.citation_id}>
                <AvatarSlot>
                  <img
                    src={imageUrl(portrait)}
                    alt=""
                    aria-hidden="true"
                    width={80}
                    height={80}
                  />
                </AvatarSlot>
                <Descriptor>
                  <Body size="sm" bold as="span">
                    {row.descriptor}
                  </Body>
                </Descriptor>
                <ButtonLink
                  href={citationHref(row.citation_id)}
                  size="xs"
                  trackClick={{
                    source: 'profile.citing_sources_card.view_click',
                  }}
                >
                  View
                </ButtonLink>
              </Row>
            );
          })}
        </Rows>
      </Card>
    </ImpressionWatcher>
  );
};

export default CitingSourcesCard;
